import React, { useImperativeHandle, useMemo, useRef } from 'react';
import { ClassNames } from '@emotion/react';
import styled from '@emotion/styled';
import { ScrollableList } from '@bucketplace/design-system';

import { RankingStoreItem, type RankingStoreItemProps } from './RankingStoreItem';

export interface RankingStoreItemListActions {
  resetScroll(): void;
}

export interface RankingStoreItemListProps {
  actions?: React.Ref<RankingStoreItemListActions>;
  /**
   * @default 4
   */
  group?: number;
  items: Pick<RankingStoreItemProps, 'href' | 'linkComponent' | 'logData' | 'productDiscount' | 'productImageUrl' | 'productName' | 'productPrice'>[];
}

export function RankingStoreItemList(props: RankingStoreItemListProps) {
  const { actions, group = 4, items } = props;
  const listRef = useRef<HTMLElement | null>(null);

  const groups = useMemo(() => {
    const result: RankingStoreItemProps[][] = [];
    for (let i = 0; i < items.length; i += group) {
      result.push(items.slice(i, i + group).map<RankingStoreItemProps>((item, j) => ({
        ...item,
        rank: i + j + 1,
      })));
    }
    return result;
  }, [items, group]);

  useImperativeHandle(actions, () => ({
    resetScroll() {
      listRef.current?.scrollTo(0, 0);
    },
  }), []);

  if (items.length === 0) {
    return null;
  }

  return (
    <ClassNames>
      { ({ css }) => (
        <ScrollableList
          gap={0}
          gradation={false}
          contentClassName={css`
            padding-right: 20px;
          `}
          contentRef={(node) => {
            listRef.current = node;
          }}
        >
          { groups.map((groupItems) => (
            <Group key={groupItems[0]?.rank}>
              { groupItems.map((item) => (
                <RankingStoreItem key={item.rank} {...item} />
              )) }
            </Group>
          )) }
        </ScrollableList>
      ) }
    </ClassNames>
  );
}

const Group = styled.div`
  display: flex;
  flex-direction: column;
  width: 280px;
  flex-shrink: 0;
  padding-bottom: 12px;
  scroll-snap-align: start;
`;
